import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { InputTextComponent, SearchBoxComponent } from '@backbase/ui-ang';
import { Subject } from 'rxjs';
import { DomAttributesService } from '@backbase/product-summary-common-ang';
import {ResourcesService} from "services";

export interface SearchPayload {
  term: string;
  items: Array<any>;
}

const possibleAccountDataKeys = ['balance', 'number', 'IBAN', 'BBAN', 'BIC', 'currency', 'bankBranchCode'];

export const isAccountData = (arg: any) => !!arg && possibleAccountDataKeys.some((key) => arg.hasOwnProperty(key));
export const isAccountDataArray = (arg: any) => Array.isArray(arg) && arg.every(isAccountData);

type GroupByFunction = (item: any) => any;
export type AccountSelectorSize = 'sm' | 'md';

@Component({
  selector: 'bn-account-selector-ui',
  templateUrl: './bn-account-selector.component.html',
  styleUrls: ['./bn-account-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [DomAttributesService],
})
export class AccountSelectorComponent<AccountSelectorItem = any> implements AfterViewInit {
  readonly hostRef: AccountSelectorComponent = this;
  readonly productNumberFormat = { length: 16, maskRange: [0, 12], segments: 4 };
  private _items: AccountSelectorItem | AccountSelectorItem[] = [];
  private _showError = false;
  notFoundTextLabel = 'No items found';
  resultCount = 0;
  public readonly statusId = this.domAttributesService.generateId();
  accountSelectorSizeClass = '';
  public styleSelect = {
    fontSize: '24px',
    color: '#007953',
    fontWeight: '300'
  }
  public styleMessage = {
    width: '56%'
  }
  /**
   * ng-select
   */
  @ViewChild(NgSelectComponent) ngSelect: NgSelectComponent | undefined;
  @ViewChild('accountSelectorModel') private accountSelectorModel: NgModel | undefined;

  @ViewChild(SearchBoxComponent) textInput: SearchBoxComponent<unknown> | undefined;
  @ViewChild(InputTextComponent) searchBox: InputTextComponent | undefined;

  /**
   * Emitter when search is performed. Outputs search term
   */
  @Output() search: EventEmitter<string> = new EventEmitter<string>();
  /**
   * Emitter when clear search
   */
  @Output() clearSearch: EventEmitter<void> = new EventEmitter<void>();
  /**
   * Emitter when scroll is triggered (will emmit scroll events if virtualScroll is enabled)
   */
  @Output() scroll: EventEmitter<{ start: number; end: number }> = new EventEmitter<{ start: number; end: number }>();
  /**
   * Emitter when scrollToEnd is triggered
   */
  @Output() scrollToEnd: EventEmitter<void> = new EventEmitter<void>();
  /**
   * Emitter when item is removed
   */
  @Output() remove: EventEmitter<any> = new EventEmitter<any>();
  /**
   * Emitter when item is added to selected items or removed from selected items.
   * When Multiple is true: Output is Items[]
   * When Multuple is false: Output is Item
   */
  @Output() change: EventEmitter<object | object[]> = new EventEmitter<object | object[]>();
  /**
   * Emitter when filter value is changed
   */
  @Output() filterChange: EventEmitter<string> = new EventEmitter<string>();
  /**
   * Emit event when account selector is focused
   */
  @Output() focus: EventEmitter<void> = new EventEmitter<void>();
  /**
   * Emit event when account selector is blurred
   */
  @Output() blur: EventEmitter<void> = new EventEmitter<void>();
  /**
   * `AccountSelectorItem`s array
   */
  @Input() set items(val: AccountSelectorItem | AccountSelectorItem[]) {
    this._items = val;
  }
  get items() {
    return this._items;
  }
  /**
   * Disabled attr for ng-select
   */
  @Input() disabled = false;

  /**
   * Not found text for ng-select
   */
  @Input() set notFoundText(val: string) {
    if (val) {
      this.notFoundTextLabel = val;
    }
  }

  /**
   * Show the error border around account selector.
   * By default, error message will be visible on invalid and touch state.
   * If user wants to show it on submit or some other scenario,
   * then by passing the value for showError, error borders can be triggered.
   */
  @Input()
  set showError(flag: boolean) {
    this._showError = flag;
    if (this.required && this.accountSelectorModel?.invalid) {
      if (this._showError) {
        this.markAccountSelectorModelAsTouched();
      } else {
        this.markAccountSelectorModelAsUnTouched();
      }
    }
  }
  get showError(): boolean {
    return this._showError;
  }
  /**
   * Selected items
   */
  @Input() selectedItems: AccountSelectorItem | AccountSelectorItem[] = [];
  /**
   * Allows to select multiple items
   */
  @Input() multiple = false;
  /**
   * Whether to close the menu when a value is selected
   */
  @Input() closeOnSelect = true;
  /**
   * Allow to search for value. Default from ng-select is `true`.
   */
  @Input() searchable = true;
  /**
   * Allow to clear selected value.
   */
  @Input() clearable = false;
  /**
   * Placeholder text
   */
  @Input() placeholder = '';
  /**
   * Loading state from the outside (async items loading)
   */
  @Input() loading = false;
  /**
   * Dropdown Position (bottom | top | auto)
   */
  @Input() dropdownPosition = 'bottom';
  /**
   * If true then `scrollEnd` event should NOT be emitted
   */
  @Input() disableScrollEnd = false;
  /**
   * Turn on or turn off bbHighlight directive in child product items
   */
  @Input() highlight = true;
  /**
   * balance label under the balance
   */
  @Input() balanceLabel = null;
  /**
   * Filter items.
   */
  @Input() filterItems = false;
  /**
   * Marks first item as focused when opening/filtering.
   */
  @Input() markFirst = false;
  /**
   * Enables NgSelect internal filtering functionality
   */
  @Input() internalFiltering = true;
  /**
   * Enables virtual scrolling mechanism
   */
  @Input() virtualScroll = false;

  /**
   * Enable required validation for ng-select
   */
  @Input() required = false;
  /**
   * Custom autocomplete or advanced filter.
   */
  @Input() typeahead?: Subject<string>;
  /**
   * Minimum term length to start a search. Should be used with typeahead
   */
  @Input() minTermLength?: number;
  /**
   * Set custom text when using Typeahead
   */
  @Input() typeToSearchText?: string;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby') ariaDescribedby: string | undefined;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-labelledby') ariaLabelledby: string | undefined;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-invalid') ariaInvalid: string | undefined = 'false';

  /**
   * Allow to apply custom search function
   */
  @Input() searchFunc: Function | undefined;

  /**
   * Will show currency Symbol in amount. Default value true
   */
  @Input() showCurrencySymbol = true;
  /**
   * Will show the bank branch code. Defaults to false
   */
  @Input() showBankBranchCode = false;

  /**
   * Allow to group items by key or function expression
   */
  @Input() groupByFn: GroupByFunction | undefined;
  /**
   * Function expression to provide group value
   */
  @Input() groupValueFn = (_: string, children: any[]) => ({
    name: _,
    total: children.length,
  });
  /**
   * Set account selector view size
   *
   * @param size
   */
  @Input() set size(size: AccountSelectorSize) {
    this.accountSelectorSizeClass = size === 'sm' ? 'bb-account-selector--sm' : '';
  }
  /**
   * Set account selector view size
   */
  @Input() compareItemsWith: (a: AccountSelectorItem, b: AccountSelectorItem) => boolean = (a, b) => a === b;

  onSearch(event: string | SearchPayload) {
    if (typeof event === 'string') {
      this.search.emit(event);
    }
  }

  onClear() {
    this.clearSearch.emit();
    this.onFilterChange(undefined);
  }

  onScroll(position: { start: number; end: number }): void {
    this.scroll.emit(position);
  }

  onScrollToEnd() {
    if (!this.disableScrollEnd) {
      this.scrollToEnd.emit();
    }
  }

  onRemove(event: any) {
    this.remove.emit(event);
  }

  onChange(event: Event | any) {
    if (event.type === 'change') {
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    if (isAccountData(event) || isAccountDataArray(event)) {
      this.change.emit(event);
    }
  }

  selectAll() {
    this.selectedItems = this.items;
    this.onChange(this.selectedItems);
  }

  unselectAll() {
    this.selectedItems = [];
    this.onChange(this.selectedItems);
  }

  resetAccountSelectorModel() {
    this.accountSelectorModel?.control.reset();
  }

  constructor(
    private readonly renderer: Renderer2,
    private readonly domAttributesService: DomAttributesService,
    private resourcesService: ResourcesService
  ) {}

  ngAfterViewInit() {
    //https://github.com/ng-select/ng-select/issues/951
    //remove role, because of accessibility issues, role=listbox need to be set only if it has child element
    //input need have aria-hidden=true, because it not vissible element
    if (this.ngSelect) {
      this.ngSelect.focus = () => this.focusEditableElement();
      this.renderer.removeAttribute(this.ngSelect.element, 'role');
      const inputContainers = this.ngSelect.element.getElementsByClassName('ng-input');
      /* eslint-disable */
      for (let i = 0; i < inputContainers.length; i++) {
        const inputElements = inputContainers[i].getElementsByTagName('input');
        /* eslint-disable */
        for (let j = 0; j < inputElements.length; j++) {
          if (this.ariaInvalid) {
            this.renderer.setAttribute(inputElements[j], 'aria-invalid', this.ariaInvalid);
          }
          if (this.ariaLabelledby) {
            this.renderer.setAttribute(inputElements[j], 'aria-labelledby', this.ariaLabelledby);
          }
          if (this.ariaDescribedby) {
            this.renderer.setAttribute(inputElements[j], 'aria-describedby', this.ariaDescribedby);
          }
          if (!(this.ariaInvalid || this.ariaLabelledby || this.ariaDescribedby)) {
            this.renderer.setAttribute(inputElements[j], 'aria-hidden', 'true');
          }
          this.renderer.addClass(inputElements[j], 'sr-only');
        }
      }
    }
  }

  onFocus() {
    this.focus.emit();
  }

  onBlur() {
    // Emit blur event only if ng-select is not open(e.g: tabbing through the account selector).
    if (!this.ngSelect?.isOpen) {
      this.blur.emit();
    }
  }

  onOpen() {
    //set role, because of accessibility issues, when child element appear in dom
    if (this.ngSelect) {
      this.renderer.setAttribute(this.ngSelect.element, 'role', 'listbox');
      this.updateResultCount();
    }
  }

  onClose() {
    //remove role, because of accessibility issues, role=listbox need to be set only if it has child element
    if (this.ngSelect) {
      this.renderer.removeAttribute(this.ngSelect.element, 'role');
      this.focusEditableElement();
      // Used to show validation(incase used inside form) message after account selector is closed
      this.blur.emit();
    }
  }

  onFilterChange(query: string | undefined): void {
    const filterValue = query || '';
    if (this.ngSelect && this.internalFiltering) {
      this.ngSelect.filter(filterValue);
    }
    this.updateResultCount();
    this.filterChange.next(filterValue);
  }

  focusEditableElement() {
    const input = this.textInput || this.searchBox;
    if (this.ngSelect && !this.ngSelect.isOpen) {
      this.focusNgSelect();
    } else if (input) {
      input.focusEditableElement();
    }
  }

  /**
   * Call the native `ngSelect` `focus()` method
   * because ngSelect focus has been override in AccountSelectorComponent ngAfterViewInit
   */
  private focusNgSelect() {
    if (this.ngSelect) {
      this.ngSelect.constructor.prototype.focus.call(this.ngSelect);
    }
  }

  private markAccountSelectorModelAsTouched() {
    this.accountSelectorModel?.control.markAsTouched();
  }
  private markAccountSelectorModelAsUnTouched() {
    this.accountSelectorModel?.control.markAsUntouched();
  }

  private updateResultCount(): void {
    this.resultCount = this.ngSelect?.itemsList.filteredItems.filter(({ children }) => !children).length || 0;
  }

  public getLocalBalance(item: any) {
    if (item?.externalKindId === 'kind5') {
      return item?.additions?.curAmtAmount;
    }
    if (item?.externalKindId === 'kind4') {
      this.resourcesService.amount$.next({value: item?.additions?.localAvailableBalance});
      return item?.additions?.localAvailableBalance;
    }
    if (item?.externalKindId === 'kind1') {
      return item?.availableBalance;
    }
    return item?.balance || item?.additions?.localAvailableBalance || item?.additions?.nextPmtFee;
  }

  public getForeignAmount(item: any) {
    if (+item?.additions?.foreignAvailableBalance === -1) {
      return null;
    }
    return item?.additions?.foreignAvailableBalance || null;
  }

  public getForeignCurrency(item: any) {
    return item?.additions?.foreignCurrencyCode || null;
  }

  public getProductNumber(item: any) {
    return item?.number || item?.additions?.creditCardAccountNumber;
  }
}
